import { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/cn";
import { useAccount, type CallingFlags } from "@/providers/AccountProvider";
import { AccountSwitcher } from "@/components/shell/AccountSwitcher";
import { BrandMark } from "@/components/brand/BrandMark";
import { TenantBrandingSync } from "@/components/brand/TenantBrandingSync";
import { PageErrorBoundary } from "@/components/shell/PageErrorBoundary";
import { WhatsAppHealthNotice } from "@/components/shell/WhatsAppHealthNotice";
import { CallDockRoot, CallLauncherSlot } from "@/calls/CallDockRoot";

// appsidebar4stylespec.md: dual rail + contextual panel. Rail and panel SHARE the same
// background (neutral-900) -- separation comes from the rail's icon rhythm and the
// darker canvas (neutral-950) beginning to the right of the panel, never from a border.
// Depth is fill, never shadow. Greyscale only; no brand accent in chrome.
//
// Radius ladder (do not deviate): 8px text-height controls, 10px square icon buttons
// and the footer row, 9999px avatars only.

import {
  InsightsNavIcon,
  ConversationsNavIcon,
  RevenueNavIcon,
  SocialNavIcon,
  AutomationNavIcon,
  WorkspaceNavIcon,
  SearchIcon,
} from "@/components/navigation/NavIcons";

interface RailItem {
  to: string;
  label: string;
  glyph: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  /** Hidden unless the signed-in seat holds this. Absent means everybody sees it. */
  needs?: string;
}

// Each rail entry owns a section of the contextual panel. `icon` provides a clean,
// minimal enterprise Lucide vector; `glyph` is preserved for fallback / test contracts.
const RAIL_ITEMS: RailItem[] = [
  { to: "/dashboard", label: "Insights", glyph: "monitoring", icon: InsightsNavIcon, needs: "reporting.view" },
  { to: "/inbox", label: "Conversations", glyph: "forum", icon: ConversationsNavIcon, needs: "conversations.view" },
  { to: "/opportunities", label: "Revenue", glyph: "view_kanban", icon: RevenueNavIcon, needs: "opportunities.view" },
  { to: "/social", label: "Social", glyph: "campaign", icon: SocialNavIcon, needs: "campaigns.view" },
  { to: "/agent-studio", label: "Automation", glyph: "smart_toy", icon: AutomationNavIcon, needs: "automations.view" },
  { to: "/settings", label: "Workspace", glyph: "tune", icon: WorkspaceNavIcon, needs: "settings.view" },
];

interface PanelLink {
  to: string;
  label: string;
  /** Same rule as RailItem.needs. A link the API would refuse is not shown: a nav that
   *  offers a 403 teaches somebody the product is broken rather than that they lack
   *  access. */
  needs?: string;
  /** Shown only where this feature is switched on for the account (see featureOn). */
  feature?: "calling";
}

export interface PanelGroup {
  label: string;
  links: PanelLink[];
}

// The contextual panel's contents per rail section. Only routes that exist are listed;
// this is the nav map, so an entry here is a promise the route resolves.
const PANEL_SECTIONS: Record<string, { title: string; groups: PanelGroup[] }> = {
  "/dashboard": {
    title: "Insights",
    groups: [
      {
        label: "Overview",
        links: [
          { to: "/dashboard", label: "Executive overview" },
          { to: "/performance", label: "Campaign performance", needs: "reporting.view" },
          { to: "/ads", label: "All ads", needs: "reporting.view" },
        ],
      },
    ],
  },
  "/inbox": {
    title: "Conversations",
    groups: [
      {
        label: "Inboxes",
        links: [
          { to: "/inbox", label: "All channels" },
          { to: "/inbox/whatsapp", label: "WhatsApp" },
          { to: "/inbox/instagram", label: "Instagram" },
          { to: "/inbox/messenger", label: "Messenger" },
        ],
      },
      {
        // A panel link, never a rail item, so no preset's landing page moves (AppShell.test).
        label: "Voice",
        links: [{ to: "/calls", label: "Calls", needs: "calls.view", feature: "calling" }],
      },
    ],
  },
  "/opportunities": {
    title: "Revenue",
    groups: [
      {
        label: "Work",
        links: [
          { to: "/opportunities", label: "Opportunities" },
          { to: "/contacts", label: "Contacts", needs: "contacts.view" },
        ],
      },
      {
        label: "Configure",
        links: [{ to: "/pipeline", label: "Pipelines", needs: "opportunities.edit" }],
      },
    ],
  },
  "/social": {
    title: "Outbound & Social",
    groups: [
      {
        label: "WhatsApp Outbound",
        links: [
          { to: "/broadcast", label: "WhatsApp Broadcast", needs: "campaigns.view" },
        ],
      },
      {
        label: "Publish & measure",
        links: [
          { to: "/social/composer", label: "Composer" },
          { to: "/social/engagement", label: "Engagement" },
        ],
      },
    ],
  },
  "/agent-studio": {
    title: "Automation",
    groups: [
      {
        label: "Build",
        links: [
          { to: "/agent-studio", label: "Agent studio" },
          { to: "/agent-studio/flows", label: "Automations" },
          { to: "/agent-studio/history", label: "History" },
          // A submission is what a customer said in a conversation, so it takes the same grant.
          { to: "/agent-studio/form-submissions", label: "Form submissions", needs: "conversations.view" },
          { to: "/agent-studio/rules", label: "Automation rules" },
        ],
      },
    ],
  },
  "/settings": {
    title: "Workspace",
    groups: [
      {
        label: "Connected accounts",
        links: [
          { to: "/connections", label: "Accounts & assets", needs: "connections.manage" },
          { to: "/catalog", label: "Catalog", needs: "settings.edit" },
          { to: "/commerce", label: "Shop orders", needs: "settings.edit" },
          { to: "/lead-forms", label: "Lead forms", needs: "settings.edit" },
        ],
      },
      {
        label: "Configure",
        links: [
          { to: "/custom-fields", label: "Custom fields", needs: "settings.edit" },
          // Gated on team.manage, so somebody who cannot staff the account never sees the
          // door. The page refuses them too -- this only stops the nav offering a click
          // that ends in a refusal.
          { to: "/settings/team", label: "Team", needs: "team.manage" },
          // API keys reach contact data from outside the app, so the door is shown to the
          // same people who may change workspace settings. The server has the final say.
          { to: "/settings/developer", label: "Developer", needs: "settings.edit" },
          // Everyone in the workspace can SEE the branding; the page itself decides who may
          // change it (settings.edit, and only once a platform admin has allowed it).
          { to: "/settings/branding", label: "Branding", needs: "settings.view" },
          { to: "/settings", label: "Preferences" },
        ],
      },
    ],
  },
};

// Several routes belong to a rail section whose path they do not share as a prefix.
// Listed explicitly, because a prefix-only match silently files them under Insights --
// the nav would still render, just in the wrong place.
const SECTION_OVERRIDES: Array<{ prefix: string; section: string }> = [
  { prefix: "/contacts", section: "/opportunities" },
  // /pipeline is now pipeline MANAGEMENT, a Configure screen inside Revenue. Without this
  // it prefix-matched nothing and fell through to the /dashboard default -- which is the
  // bug /opportunities had: it highlighted Insights over a Kanban board.
  { prefix: "/pipeline", section: "/opportunities" },
  { prefix: "/broadcast", section: "/social" },
  { prefix: "/connections", section: "/settings" },
  { prefix: "/catalog", section: "/settings" },
  { prefix: "/commerce", section: "/settings" },
  { prefix: "/lead-forms", section: "/settings" },
  { prefix: "/custom-fields", section: "/settings" },
  // /ads and /performance are Insights, which is also the fallback -- named anyway so
  // the mapping is stated rather than relying on the default.
  { prefix: "/ads", section: "/dashboard" },
  { prefix: "/performance", section: "/dashboard" },
  { prefix: "/calls", section: "/inbox" },
];

/** `/accounts/orWQlK0M8BCmMmkqu3cc/inbox/whatsapp` -> `/inbox/whatsapp`.
 *
 *  Matched on the id's actual shape rather than by dropping two segments: a positional
 *  rule would silently eat the first segment of any future non-account route. */
export function stripAccount(pathname: string): string {
  const match = pathname.match(/^\/accounts\/[A-Za-z0-9]{20}(?=\/|$)/);
  return match ? pathname.slice(match[0].length) || "/" : pathname;
}

/**
 * The first screen this permission set can actually open.
 *
 * Landing somebody on /dashboard was a guess, and for a Limited user it is the ONE page
 * they cannot open — `reporting.view` is not in their preset, so signing in or switching
 * accounts greeted them with "Your role does not allow this: reporting.view".
 *
 * Read off RAIL_ITEMS in order, so the answer is the top-most section they can reach and
 * a new rail entry needs no second list kept in step with this one.
 */
export function firstAllowedPath(permissions: readonly string[]): string {
  const granted = new Set(permissions);
  const reachable = RAIL_ITEMS.find((item) => !item.needs || granted.has(item.needs));
  // Nothing at all is only possible by overriding every permission off one seat. The
  // preferences screen is about the PERSON rather than the account, so it is the least
  // wrong place to be standing while somebody sorts that out.
  return reachable?.to ?? "/settings";
}

export function sectionKeyForPath(pathname: string): string {
  const override = SECTION_OVERRIDES.find((entry) => pathname.startsWith(entry.prefix));
  if (override) return override.section;
  const match = RAIL_ITEMS.find((item) => pathname.startsWith(item.to));
  return match?.to ?? "/dashboard";
}

/**
 * Whether a feature-gated link may show. Calling (D7): only where it is live for the account,
 * or -- before the first number is on -- to somebody who can switch one on, so Members never
 * meet an empty section in an account that cannot take calls.
 */
function featureOn(
  feature: NonNullable<PanelLink["feature"]>,
  can: (permission: string) => boolean,
  calling: CallingFlags,
): boolean {
  switch (feature) {
    case "calling":
      return calling.platform && (calling.enabled || can("calls.manage"));
  }
}

/**
 * The panel's groups for a section, as this seat may see them, matching the filter text.
 *
 * Gated links are dropped whether or not anything is typed -- the panel used to return every
 * link unfiltered while the box was empty -- and a group left with no links is dropped with
 * them, so no heading stands over nothing.
 */
export function visiblePanelGroups(
  sectionKey: string,
  query: string,
  can: (permission: string) => boolean,
  calling: CallingFlags,
): PanelGroup[] {
  const section = PANEL_SECTIONS[sectionKey] ?? PANEL_SECTIONS["/dashboard"];
  const needle = query.trim().toLowerCase();
  return (section?.groups ?? [])
    .map((group) => ({
      ...group,
      links: group.links.filter(
        (link) =>
          (!link.needs || can(link.needs)) &&
          (!link.feature || featureOn(link.feature, can, calling)) &&
          (!needle || link.label.toLowerCase().includes(needle)),
      ),
    }))
    .filter((group) => group.links.length > 0);
}

function IconRail({ activeSection }: { activeSection: string }) {
  const { path, can } = useAccount();
  return (
    // overflow-hidden is a guard, not a layout fix: the rail is already a fixed
    // 56px flex item. It exists so nothing can ever spill past the rail into the
    // panel again the way unrendered icon ligatures did.
    <aside className="flex w-14 shrink-0 flex-col overflow-hidden bg-n-surface" aria-label="Primary">
      {/* Brand block: the workspace's own logo when it is white-labelled, ORAI's otherwise. */}
      <div className="flex h-14 items-center justify-center">
        <NavLink
          to={path("/dashboard")}
          aria-label="Home"
          className="flex h-8 max-w-12 items-center justify-center rounded-[var(--radius-ctl)] transition-opacity hover:opacity-90 active:scale-95"
        >
          <BrandMark size={32} />
        </NavLink>
      </div>

      <nav className="flex flex-1 flex-col items-center gap-1 pb-3">
        {RAIL_ITEMS.filter((item) => !item.needs || can(item.needs)).map((item) => {
          const isActive = activeSection === item.to;
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={path(item.to)}
              title={item.label}
              aria-label={item.label}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "flex size-10 items-center justify-center rounded-[var(--radius-icon)] transition-colors duration-150 ease-out",
                isActive
                  ? "bg-n-hover text-n-fg"
                  : "text-n-fg-faint hover:bg-brand-subtle hover:text-n-fg",
              )}
            >
              <Icon size={18} />
            </NavLink>
          );
        })}
      </nav>

      {/* The phone launcher: opens the floating call panel. Renders nothing unless this seat
          may answer calls here (dockAllowed). */}
      <CallLauncherSlot />

      {/* Rail footer band -- matches the panel footer so the two read as one strip. */}
      <div className="h-[60px] w-14 border-t border-hairline border-n-line/50 bg-brand-subtle/50" />
    </aside>
  );
}

function ContextPanel({ activeSection, onOpenPalette }: { activeSection: string; onOpenPalette: () => void }) {
  const { path, can, calling } = useAccount();
  const [query, setQuery] = useState("");
  const section = PANEL_SECTIONS[activeSection] ?? PANEL_SECTIONS["/dashboard"];

  const groups = useMemo(
    () => visiblePanelGroups(activeSection, query, can, calling),
    [activeSection, query, can, calling],
  );

  return (
    <div className="hidden w-64 shrink-0 flex-col bg-n-surface md:flex">
      <div className="flex h-14 items-center justify-between gap-2 pl-5 pr-2">
        <h2 className="text-[16px] font-medium tracking-[-0.01em] text-n-fg">{section?.title ?? "Insights"}</h2>
        <button
          type="button"
          onClick={onOpenPalette}
          title="Search (Ctrl+K)"
          aria-label="Open command palette"
          className="flex size-8 items-center justify-center rounded-[var(--radius-ctl)] text-n-fg-faint transition-colors duration-150 ease-out hover:bg-brand-subtle hover:text-n-fg"
        >
          <SearchIcon size={14} />
        </button>
      </div>

      <div className="px-2 pb-2">
        <div className="relative">
          <SearchIcon
            size={15}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-n-fg-faint"
          />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter"
            aria-label="Filter navigation"
            className="h-9 w-full rounded-[var(--radius-ctl)] border-hairline border-n-line bg-n-canvas pl-9 pr-3 text-[13px] text-n-fg outline-none transition-colors duration-150 ease-out placeholder:text-n-fg-faint focus:border-brand-primary"
          />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2">
        {groups.map((group) => (
          <div key={group.label} className="mb-4">
            <p className="mb-1 px-3 text-[11px] font-medium uppercase tracking-[0.05em] text-n-fg-faint">
              {group.label}
            </p>
            {group.links.map((link) => (
              <NavLink
                key={link.to}
                to={path(link.to)}
                end
                className={({ isActive }) =>
                  cn(
                    "flex h-8 w-full items-center rounded-[var(--radius-ctl)] px-3 text-[13px] transition-colors duration-150 ease-out",
                    isActive
                      ? "bg-n-hover font-medium text-n-fg"
                      : "font-normal text-n-fg-muted hover:bg-brand-subtle hover:text-n-fg",
                  )
                }
              >
                {link.label}
              </NavLink>
            ))}
          </div>
        ))}
        {groups.length === 0 && query.trim() && (
          <p className="px-3 text-[13px] text-n-fg-faint">Nothing matches that filter.</p>
        )}
      </nav>

      <div className="border-t border-hairline border-n-line/50 bg-brand-subtle/50 p-2">
        {/* Unified workspace & account switcher with user profile, settings, and logout */}
        <AccountSwitcher />
      </div>
    </div>
  );
}

export function AppShell({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { pathname } = useLocation();
  // The rail highlights by the path INSIDE the account, so the account id never takes
  // part in the match.
  const activeSection = sectionKeyForPath(stripAccount(pathname));

  return (
    <div className="flex h-[100dvh] overflow-hidden bg-n-canvas text-n-fg">
      <IconRail activeSection={activeSection} />
      <ContextPanel activeSection={activeSection} onOpenPalette={onOpenPalette} />
      <TenantBrandingSync />
      {/* Outside <main> and the page's boundary: moving around the account never unmounts it,
          a page crash leaves it up, and its own boundary keeps a dock crash off the shell. It
          draws nothing over the page until the rail's launcher opens it or a call rings. */}
      <CallDockRoot />
      <main className="min-w-0 flex-1 overflow-y-auto bg-n-canvas">
        <WhatsAppHealthNotice />
        {/* A page that throws costs that page; the rail and panel stay usable. */}
        <PageErrorBoundary resetKey={pathname}>
          <Outlet />
        </PageErrorBoundary>
      </main>
    </div>
  );
}

// Same geometry as the resolved shell so nothing shifts when routes resolve (no CLS).
export function ShellSkeleton() {
  return (
    <div className="flex h-[100dvh] overflow-hidden bg-n-canvas">
      <div className="w-14 shrink-0 bg-n-surface" />
      <div className="hidden w-64 shrink-0 bg-n-surface md:block" />
      <div className="min-w-0 flex-1 bg-n-canvas" />
    </div>
  );
}
